/**
 * 逐次検定（途中経過を見ながら進める実験）の棄却限界。
 *
 * 実験の途中で何度も結果を見て「p < 0.05 になった時点で止める」運用をすると、
 * 見た回数だけ棄却の機会が増える。多重比較と同じ構造の誤りで、
 * 5% のつもりの偽陽性率は 5 回覗けば実際には 15% 前後まで膨らむ。
 *
 * ここでは Lan-DeMets のアルファ消費関数を使い、確認のたびに使ってよい
 * 有意水準を配分する。全体で消費する有意水準は、何回覗いても alpha に収まる。
 *
 * 棄却限界は、途中で止まらずに進んだ場合の統計量の同時分布から求める。
 * 情報時間 t での統計量は、標準化前のブラウン運動 B(t)（分散 t、独立増分）と
 * 見なせるので、「まだ止まっていない」条件付き密度を数値積分で持ち回り、
 * 各回の脱出確率が配分された有意水準に一致する限界値を二分探索で解く。
 */

import { normalPdf, normalQuantile, normalSf } from './normal'
import type { Tail } from './types'

/** アルファ消費関数の種類。 */
export type SpendingFunction = 'obrien-fleming' | 'pocock'

export const SPENDING_LABELS: Record<SpendingFunction, string> = {
  'obrien-fleming': "O'Brien-Fleming",
  pocock: 'Pocock',
}

/** 継続領域を刻む点数。Simpson 則のために奇数にする。 */
const GRID_POINTS = 401

/** 片側検定で下側に伸ばす標準偏差の倍数。この外側の確率は 1e-15 未満。 */
const TAIL_SIGMA = 8

/** 二分探索の反復回数。倍精度で限界値が動かなくなるまで回す。 */
const BISECTION_STEPS = 200

export interface Boundary {
  /** 情報時間 (0, 1]。予定サンプルサイズに対する進捗。 */
  informationTime: number
  /** この回までに累積で消費した有意水準 */
  cumulativeAlpha: number
  /** この回に新しく配分された有意水準 */
  incrementalAlpha: number
  /** 棄却限界の z 値 */
  z: number
  /**
   * この回に有意と言うために p 値が下回るべき値。
   * 検定の裾に合わせてあるので、観測された p 値と直接比べられる。
   */
  nominalAlpha: number
}

export interface BoundaryInput {
  /** 実験全体で使う有意水準 */
  alpha: number
  /** 各確認時点の情報時間。昇順で、いずれも (0, 1]。 */
  informationTimes: readonly number[]
  tail: Tail
  spending: SpendingFunction
}

/**
 * 情報時間 t までに消費してよい有意水準の累積。
 *
 * O'Brien-Fleming 型は序盤をほとんど消費せず、早期に止めるには
 * 極端に強い証拠を要求する（そのぶん最後まで進んだときの水準が落ちない）。
 * Pocock 型は毎回ほぼ同じ水準を配分するので早期に止まりやすいが、
 * 最後まで進んだ場合の水準は厳しいままになる。
 */
export function spentAlpha(
  t: number,
  alpha: number,
  spending: SpendingFunction,
  tail: Tail,
): number {
  if (t <= 0) return 0
  if (t >= 1) return alpha
  if (spending === 'pocock') {
    return alpha * Math.log(1 + (Math.E - 1) * t)
  }
  // O'Brien-Fleming 型 (Lan-DeMets)。両側は片側 alpha/2 を左右に配分する。
  const oneSided = tail === 'two-sided' ? alpha / 2 : alpha
  const spentOneSided = normalSf(normalQuantile(1 - oneSided) / Math.sqrt(t))
  return tail === 'two-sided' ? 2 * spentOneSided : spentOneSided
}

/** 等間隔の情報時間。k 回目の確認は t = k / looks。 */
export function equallySpacedTimes(looks: number): number[] {
  return Array.from({ length: looks }, (_, i) => (i + 1) / looks)
}

/** 継続領域上の密度。値は等間隔の格子点で持つ。 */
interface Density {
  lo: number
  hi: number
  /** 格子点の値。長さは GRID_POINTS（奇数）。 */
  values: number[]
}

/** Simpson 則で密度に重みを掛けた積分を求める。 */
function integrate(density: Density, weight: (x: number) => number): number {
  const { lo, hi, values } = density
  const n = values.length - 1
  const h = (hi - lo) / n
  let sum = 0
  for (let i = 0; i <= n; i++) {
    const coefficient = i === 0 || i === n ? 1 : i % 2 === 1 ? 4 : 2
    sum += coefficient * values[i] * weight(lo + i * h)
  }
  return (sum * h) / 3
}

/**
 * 直近の計算結果。棄却限界は観測データに依存せず、
 * 有意水準・確認時点・裾・消費関数だけで決まる。画面では人数を 1 文字
 * 打つたびに分析全体が回るため、同じ設定での再計算を避ける。
 */
const cache = new Map<string, Boundary[] | null>()
const CACHE_LIMIT = 64

/**
 * アルファ消費関数から各確認時点の棄却限界を求める。
 *
 * 情報時間が昇順でない、範囲外、あるいは配分された有意水準が
 * 到達不能なほど大きい場合は null を返す。
 */
export function computeBoundaries(input: BoundaryInput): Boundary[] | null {
  const key = `${input.alpha}|${input.tail}|${input.spending}|${input.informationTimes.join(',')}`
  if (cache.has(key)) return cache.get(key)!
  const result = computeBoundariesUncached(input)
  // 上限を超えたら最も古い分から捨てる。設定の組み合わせは高々数十通り。
  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value!)
  cache.set(key, result)
  return result
}

function computeBoundariesUncached(input: BoundaryInput): Boundary[] | null {
  const { alpha, informationTimes, tail, spending } = input
  const k = informationTimes.length
  if (k < 1) return null
  if (!(alpha > 0) || alpha >= 1) return null

  let previous = 0
  for (const t of informationTimes) {
    if (!Number.isFinite(t) || t <= previous || t > 1) return null
    previous = t
  }

  const twoSided = tail === 'two-sided'
  const boundaries: Boundary[] = []

  let cumulativeBefore = 0
  let density: Density | null = null

  for (let i = 0; i < k; i++) {
    const t = informationTimes[i]
    const cumulative = spentAlpha(t, alpha, spending, tail)
    const incremental = cumulative - cumulativeBefore
    if (incremental <= 0) return null

    let critical: number
    if (density === null) {
      // 初回は条件がないので、そのまま正規分布の分位点で解ける
      const sigma = Math.sqrt(t)
      critical =
        sigma * normalQuantile(1 - (twoSided ? incremental / 2 : incremental))
    } else {
      const sigma = Math.sqrt(t - informationTimes[i - 1])
      const exitProbability = (c: number) =>
        integrate(density!, (y) =>
          twoSided
            ? normalSf((c - y) / sigma) + normalSf((c + y) / sigma)
            : normalSf((c - y) / sigma),
        )
      // 限界値を 0 まで下げても配分を使い切れないなら、この消費関数では組めない
      if (exitProbability(0) < incremental) return null
      critical = solveBoundary(exitProbability, incremental, density, sigma)
    }

    boundaries.push({
      informationTime: t,
      cumulativeAlpha: cumulative,
      incrementalAlpha: incremental,
      z: critical / Math.sqrt(t),
      nominalAlpha: twoSided
        ? 2 * normalSf(critical / Math.sqrt(t))
        : normalSf(critical / Math.sqrt(t)),
    })

    cumulativeBefore = cumulative
    // 最後の確認のあとは継続しないので、密度を作り直す必要はない
    if (i < k - 1) {
      const lo = twoSided ? -critical : -TAIL_SIGMA * Math.sqrt(t)
      density =
        density === null
          ? initialDensity(lo, critical, t)
          : convolve(
              density,
              Math.sqrt(t - informationTimes[i - 1]),
              lo,
              critical,
            )
    }
  }

  return boundaries
}

/** 脱出確率が配分された有意水準に一致する限界値を二分探索で解く。 */
function solveBoundary(
  exitProbability: (c: number) => number,
  target: number,
  density: Density,
  sigma: number,
): number {
  let low = 0
  let high = Math.max(Math.abs(density.lo), Math.abs(density.hi)) + TAIL_SIGMA * sigma
  for (let step = 0; step < BISECTION_STEPS; step++) {
    const mid = (low + high) / 2
    // 脱出確率は限界値について単調減少する
    if (exitProbability(mid) > target) low = mid
    else high = mid
    if (high - low < 1e-14) break
  }
  return (low + high) / 2
}

/**
 * 初回の確認で「まだ止まっていない」場合の B(t) の密度。
 * 条件が付く前なので、そのまま分散 t の正規分布を継続領域に載せればよい。
 * 片側検定では下側に境界がないため、確率が無視できる範囲まで格子を伸ばす。
 */
function initialDensity(lo: number, hi: number, t: number): Density {
  const sigma = Math.sqrt(t)
  const h = (hi - lo) / (GRID_POINTS - 1)
  const values: number[] = new Array(GRID_POINTS)
  for (let i = 0; i < GRID_POINTS; i++) {
    values[i] = normalPdf((lo + i * h) / sigma) / sigma
  }
  return { lo, hi, values }
}

/**
 * 継続領域に残った密度を増分の正規分布で畳み込み、
 * 次の継続領域に載せ替える。ブラウン運動の増分が独立なので、
 * 「前回まで止まらなかった」条件付き密度はこの畳み込みで持ち回れる。
 */
function convolve(
  previous: Density,
  sigma: number,
  lo: number,
  hi: number,
): Density {
  const h = (hi - lo) / (GRID_POINTS - 1)
  const values: number[] = new Array(GRID_POINTS)
  for (let i = 0; i < GRID_POINTS; i++) {
    const x = lo + i * h
    values[i] = integrate(previous, (y) => normalPdf((x - y) / sigma) / sigma)
  }
  return { lo, hi, values }
}
